import React, { useState } from 'react';
import api from '../api/axios';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORIES = ['Bug', 'Idea', 'Other'];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState('Other');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!open) return null;

  const reset = () => {
    setRating(0); setCategory('Other'); setMessage(''); setError(''); setDone(false);
  };
  const close = () => { reset(); onClose(); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!message.trim()) { setError('Please write a short message.'); return; }
    setSending(true);
    try {
      await api.post('/api/parent/feedback', { rating: rating || null, category, message: message.trim() });
      setDone(true);
      setTimeout(close, 1600);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not send feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={close}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6">
            <div className="text-5xl mb-2">🎉</div>
            <p className="font-display font-bold text-gray-800">Thanks for your feedback!</p>
            <p className="text-sm text-gray-500 mt-1">We really appreciate it.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-extrabold text-gray-800 text-lg">💬 Send Feedback</h3>
              <button type="button" onClick={close} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">How's your experience?</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n === rating ? 0 : n)}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    className="text-2xl leading-none transition"
                    style={{ filter: n <= rating ? 'none' : 'grayscale(1) opacity(0.4)' }}
                  >
                    ⭐
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full mt-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">Your message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Tell us what's working well or what we can improve…"
                className="w-full mt-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
              />
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex gap-2 justify-end">
              <button type="button" onClick={close} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                type="submit"
                disabled={sending}
                className="bg-parentaccent hover:opacity-90 text-white text-sm font-bold px-5 py-2 rounded-lg disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
